import { FunctionComponent, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { faPencil, faTrash, faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";
import {
	overLongPersonSearchTitles,
	PERSON_SEARCH_TITLE_MAX_LENGTH,
} from "@signalist/shared-types";

import { Icon } from "@/components";
import { Information } from "@/components/Information/Information";
import { TextArea } from "@/components/TextArea/TextArea";
import { classNames } from "@/utils/classNames";

import { JobTitleAutocomplete } from "../../components/JobTitleAutocomplete";
import { excludeTitle, includeTitle } from "../../constants/general-filter";
import { ScoreCriteriaType } from "../../schema/score.schema";
import { isTempId } from "../../shell/configDraft";
import { DetailHeader } from "../../shell/DetailHeader";
import { PersonaSegmentTable } from "../../shell/PersonaSegmentTable";
import { MatrixCellActions } from "../../shell/ScoreMatrix/MatrixCell";
import { scoringOptions } from "../../shell/ScoreMatrix/matrixModel";
import { PersonaView, SegmentGroup } from "../../shell/scoreConfigView";
import { ConfigDraftApi } from "../../shell/useConfigDraft";
import { useFlashOnFocus } from "../../shell/useFocusTarget";

interface PersonaDetailProps extends MatrixCellActions {
	persona: PersonaView;
	edits: ConfigDraftApi;
	criteria: ScoreCriteriaType[];
	flash: boolean;
	onFlashDone: () => void;
	onDelete: (persona: PersonaView) => void;
}

const ACTION =
	"flex h-6 cursor-pointer items-center gap-1 rounded-md border px-2 text-[11px] font-medium";

const GROUP_LABEL: Record<SegmentGroup, string> = {
	PROSPECT: "prospects",
	PARTNER: "partners",
	INVESTOR: "investors",
};

/** Titles that appear in both lists — exclude wins, so they never match. */
export const clashingTitles = (include: string[], exclude: string[]): string[] => {
	const excluded = new Set(exclude.map((title) => title.trim().toLowerCase()));

	return include.filter((title) => excluded.has(title.trim().toLowerCase()));
};

/**
 * The card under the chip row for the selected persona (PREC-4055): activate
 * toggle, editable name, functional description, example titles to include
 * and exclude, and which segments the persona exists in with which scoring.
 */
export const PersonaDetail: FunctionComponent<PersonaDetailProps> = ({
	persona,
	edits,
	criteria,
	flash,
	onFlashDone,
	onDelete,
	...cellActions
}) => {
	const { t } = useTranslation();
	const cardRef = useRef<HTMLDivElement>(null);
	const [renameSignal, setRenameSignal] = useState(0);
	const unsaved = isTempId(persona.id);
	const clash = clashingTitles(persona.jobsToInclude, persona.jobsToExclude);
	// The people search refuses a title over its limit (PREC-4987): said here, before a save or a run meets it.
	const overLong = overLongPersonSearchTitles(persona.jobsToInclude);

	useFlashOnFocus(cardRef, flash, onFlashDone);

	const rows = useMemo(
		() =>
			edits.segments
				.filter((segment) => segment.active)
				.map((segment) => ({
					segment,
					persona,
					linked: edits.isLinked(segment, persona),
					assignment: edits.assignmentOf(segment, persona),
				})),
		[edits, persona]
	);
	const options = useMemo(() => scoringOptions(criteria), [criteria]);

	const titlesRow = (
		label: string,
		info: string,
		values: string[],
		field: "jobsToInclude" | "jobsToExclude",
		typeOfValue: typeof includeTitle
	) => (
		<div className="grid grid-cols-[186px_minmax(0,1fr)] items-start gap-2.5 border-t border-border-light py-2.5">
			<div className="flex items-center gap-1 pt-1 text-xs text-text-meta">
				{label}
				<Information information={info} />
			</div>
			<div data-testid={`persona-${field}`}>
				<JobTitleAutocomplete
					handleChange={(next) => edits.updatePersona(persona.id, { [field]: next })}
					typeOfValue={typeOfValue}
					values={values}
				/>
			</div>
		</div>
	);

	return (
		<div
			ref={cardRef}
			className="mt-3 overflow-hidden rounded-lg border border-border-light bg-white"
			data-testid={`persona-detail-${persona.id}`}
		>
			<DetailHeader
				active={persona.active}
				activeTitle={t("leadScoring.personas.personaActive")}
				inactiveTitle={t("leadScoring.personas.personaInactive")}
				name={persona.name}
				renameSignal={renameSignal}
				testId="persona-detail"
				actions={
					<>
						<button
							className={classNames(
								ACTION,
								"border-transparent text-text-meta hover:bg-background-light"
							)}
							type="button"
							onClick={() => setRenameSignal((n) => n + 1)}
						>
							<Icon className="h-3 w-3" icon={faPencil} />
							{t("leadScoring.detail.rename")}
						</button>
						<button
							className={classNames(
								ACTION,
								"border-status-error-light text-status-error hover:bg-error-light"
							)}
							data-testid="persona-delete"
							type="button"
							onClick={() => onDelete(persona)}
						>
							<Icon className="h-3 w-3" icon={faTrash} />
							{t("leadScoring.detail.deletePermanently")}
						</button>
					</>
				}
				onRename={(name) => edits.rename("persona", persona, name)}
				onToggle={(active) => edits.updatePersona(persona.id, { active })}
			/>
			<div className={classNames("px-3.5 py-3.5", !persona.active && "opacity-60")}>
				<TextArea
					className="min-h-[80px]"
					handleChange={(event) =>
						edits.updatePersona(persona.id, { description: event.target.value })
					}
					name={`persona-description-${persona.id}`}
					placeholder={t("leadScoring.personas.descriptionPlaceholder")}
					showError={false}
					value={persona.description}
				/>
				<div className="mt-2.5">
					{titlesRow(
						t("leadScoring.personas.includeLabel"),
						t("leadScoring.personas.includeInfo"),
						persona.jobsToInclude,
						"jobsToInclude",
						includeTitle
					)}
					{titlesRow(
						t("leadScoring.personas.excludeLabel"),
						t("leadScoring.personas.excludeInfo"),
						persona.jobsToExclude,
						"jobsToExclude",
						excludeTitle
					)}
					{clash.length > 0 && (
						<div
							className="mt-1.5 flex items-start gap-2 rounded-lg border border-tag-warning-border bg-background-warning px-3 py-2 text-xs text-tag-warning-dark"
							data-testid="persona-title-clash"
						>
							<Icon className="mt-0.5 h-3 w-3 shrink-0" icon={faTriangleExclamation} />
							{t("leadScoring.personas.clash", { titles: clash.join(", ") })}
						</div>
					)}
					{overLong.length > 0 && (
						<div
							className="mt-1.5 flex items-start gap-2 rounded-lg border border-status-error-light bg-error-light px-3 py-2 text-xs text-status-error"
							data-testid="persona-title-too-long"
							role="alert"
						>
							<Icon className="mt-0.5 h-3 w-3 shrink-0" icon={faTriangleExclamation} />
							{t("leadScoring.shell.titleTooLongInline", {
								count: overLong.length,
								limit: PERSON_SEARCH_TITLE_MAX_LENGTH,
							})}
						</div>
					)}
				</div>
				<div className="mb-2 mt-4 text-[10px] font-bold uppercase tracking-wider text-text-disabled">
					{t("leadScoring.personas.segmentsOfPersona")}
				</div>
				{unsaved ? (
					<p className="text-xs text-text-meta" data-testid="persona-save-first">
						{t("leadScoring.personas.saveFirst")}
					</p>
				) : (
					<PersonaSegmentTable
						axis="segment"
						options={options}
						renderMeta={(row) => t(`leadScoring.matrix.${GROUP_LABEL[row.segment.group]}`)}
						rows={rows}
						onToggleLink={edits.setLinked}
						{...cellActions}
					/>
				)}
			</div>
		</div>
	);
};
